import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { ApiError } from '../../lib/api';
import { useCurrentUser } from '../auth';
import { isPlausiblePhoneNumber } from '../../lib/validatePhone';
import { updateMyProfile, type ProfileUpdatePayload } from './api';

// Settings → My Profile. Lets an AM fill in the identity that appears in the
// franchisee-facing preview email signature: full name + job title + phone,
// plus an optional signature override that replaces the auto-built block.
// Reads current values from the ['me'] cache (CurrentUserProvider) and writes
// via PATCH /api/me/profile; on success it primes + invalidates ['me'] so the
// rest of the app (and a re-open of this form) sees the new values.
//
// Empty input clears a field (server maps "" -> null). The form sends all four
// fields every save -- simpler than diffing, and the server treats a present
// key as set-or-clear either way.

export function UserProfileSettings() {
  const user = useCurrentUser();
  const qc = useQueryClient();

  const [fullName, setFullName] = useState(user.fullName ?? '');
  const [jobTitle, setJobTitle] = useState(user.jobTitle ?? '');
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber ?? '');
  const [emailSignature, setEmailSignature] = useState(
    user.emailSignature ?? '',
  );
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Keep the inputs in sync if the cached user changes underneath us (e.g.
  // another tab saved, or the query refetched on focus).
  useEffect(() => {
    setFullName(user.fullName ?? '');
    setJobTitle(user.jobTitle ?? '');
    setPhoneNumber(user.phoneNumber ?? '');
    setEmailSignature(user.emailSignature ?? '');
  }, [user.fullName, user.jobTitle, user.phoneNumber, user.emailSignature]);

  const mut = useMutation({
    mutationFn: (payload: ProfileUpdatePayload) => updateMyProfile(payload),
    onSuccess: (updated) => {
      // Prime the cache with the server's canonical record, then invalidate so
      // any other readers refetch.
      qc.setQueryData(['me'], updated);
      void qc.invalidateQueries({ queryKey: ['me'] });
      setSaved(true);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : String(err)),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSaved(false);
    const phone = phoneNumber.trim();
    if (phone && !isPlausiblePhoneNumber(phone)) {
      setError(
        'Enter a valid phone number (digits, spaces, +, -, parentheses; at least 6 digits).',
      );
      return;
    }
    setError(null);
    mut.mutate({
      fullName: fullName.trim() || null,
      jobTitle: jobTitle.trim() || null,
      phoneNumber: phone || null,
      emailSignature: emailSignature.trim() || null,
    });
  };

  return (
    <form className="max-w-xl space-y-4" onSubmit={onSubmit}>
      <p className="text-sm text-slate-500">
        Your sign-in email (<span className="font-medium">{user.email}</span>)
        is used as the reply-to address on preview emails and can't be changed
        here.
      </p>

      <label className="block">
        <span className="mb-1 block text-xs font-medium text-slate-500">
          Full name
        </span>
        <input
          type="text"
          value={fullName}
          onChange={(e) => {
            setFullName(e.target.value);
            setSaved(false);
          }}
          placeholder="e.g. Scott Hambly"
          className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
        />
        <span className="mt-1 block text-xs text-slate-400">
          Shown under “Regards,” in preview emails. Falls back to “Kyck Account
          Manager” when blank.
        </span>
      </label>

      <label className="block">
        <span className="mb-1 block text-xs font-medium text-slate-500">
          Job title (optional)
        </span>
        <input
          type="text"
          value={jobTitle}
          onChange={(e) => {
            setJobTitle(e.target.value);
            setSaved(false);
          }}
          placeholder="e.g. Account Manager"
          className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
        />
      </label>

      <label className="block">
        <span className="mb-1 block text-xs font-medium text-slate-500">
          Phone number (optional)
        </span>
        <input
          type="tel"
          value={phoneNumber}
          onChange={(e) => {
            setPhoneNumber(e.target.value);
            setSaved(false);
          }}
          placeholder="e.g. 0412 345 678 or +64 21 555 0000"
          className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
        />
        {/*
          ⚠️⚠️ CLIENT-FACING, AND THIS LINE IS THE ONLY PLACE THAT CONSEQUENCE IS
          VISIBLE WHERE THE DECISION IS MADE (§5a, 2026-09-04). Since the portal home
          page publishes the industry team, editing this number changes what every
          portal client in your industry sees — and the consequence is invisible
          both here and at the moment somebody assigns an account manager.

          ⚠️ Scott chose the internal field over adding a separate client-facing one,
          knowingly. The codebase drew the opposite conclusion once before, for the
          same reason: `Account.phone` exists precisely as a public number,
          "deliberately NOT the primary contact's number (the wrong thing to
          publish)". This is the record that we went the other way here.

          ⚠️ A BLANK FIELD PUBLISHES NOTHING — never a fallback to another number.
        */}
        <span className="mt-1 block text-xs text-slate-500">
          Shown in the preview email signature, and to clients on their portal home page.
          Leave blank to publish no number.
        </span>
      </label>

      <label className="block">
        <span className="mb-1 block text-xs font-medium text-slate-500">
          Email signature override (optional)
        </span>
        <textarea
          value={emailSignature}
          rows={4}
          onChange={(e) => {
            setEmailSignature(e.target.value);
            setSaved(false);
          }}
          placeholder="Leave blank to use the default Regards / name / email / phone block."
          className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
        />
        <span className="mt-1 block text-xs text-slate-400">
          When set, this replaces the default signature block in preview emails
          verbatim.
        </span>
      </label>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {saved && !mut.isPending && (
        <p className="text-sm text-green-600">Profile saved.</p>
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={mut.isPending}>
          {mut.isPending ? 'Saving…' : 'Save profile'}
        </Button>
      </div>
    </form>
  );
}
